import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Stack, TextField, Alert, Paper,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Autocomplete, FormControl, InputLabel, Select, MenuItem, CircularProgress, Grid, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import {
  Refresh, Message, AddShoppingCart, Send, WhatsApp, Sms, Notifications as NotifIcon,
  People, Person, Groups, CheckCircle, ErrorOutline, InfoOutlined
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import { marketingAPI, usersAPI, gymsAPI, batchesAPI } from '../services/api';
import { maskPhone } from '../utils/masking';
import PaginationBar from '../components/PaginationBar';
import Notifications from './Notifications';

export default function Marketing() {
  const [searchParams, setSearchParams] = useSearchParams();

  const getTabFromQuery = (q) => {
    if (q === 'whatsapp') return 1;
    if (q === 'sms') return 2;
    if (q === 'logs' || q === 'log') return 3;
    if (q === 'notification' || q === 'notifications' || q === 'push') return 0;
    return 0;
  };

  const [tab, setTab] = useState(() => getTabFromQuery(searchParams.get('tab')));
  const [templates, setTemplates] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Pagination states for Message Logs
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Message Quota & Top Up states
  const [messageQuota, setMessageQuota] = useState({ maxMessages: 100, sentMessages: 0, remainingMessages: 100, limitReached: false });
  const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
  const [topUpPackage, setTopUpPackage] = useState(100);
  const [toppingUp, setToppingUp] = useState(false);

  // Users & Batches for selection
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // WhatsApp Tab state
  const [waSendMode, setWaSendMode] = useState('selected'); // 'selected' | 'group' | 'all'
  const [waSelectedUsers, setWaSelectedUsers] = useState([]);
  const [waSelectedGroups, setWaSelectedGroups] = useState([]);
  const [waAudience, setWaAudience] = useState('all'); // 'all' | 'active' | 'expired'
  const [waTemplateKey, setWaTemplateKey] = useState('');
  const [waSending, setWaSending] = useState(false);

  // SMS Tab state
  const [smsSendMode, setSmsSendMode] = useState('selected'); // 'selected' | 'group' | 'all'
  const [smsSelectedUsers, setSmsSelectedUsers] = useState([]);
  const [smsSelectedGroups, setSmsSelectedGroups] = useState([]);
  const [smsAudience, setSmsAudience] = useState('all'); // 'all' | 'active' | 'expired'
  const [smsTemplateKey, setSmsTemplateKey] = useState('');
  const [smsSending, setSmsSending] = useState(false);

  useEffect(() => {
    loadQuota();
    loadUsersAndBatches();
  }, []);

  useEffect(() => {
    const q = searchParams.get('tab');
    if (q) {
      setTab(getTabFromQuery(q));
    }
  }, [searchParams]);

  useEffect(() => {
    if (tab === 3) {
      loadLogs();
    } else if (tab === 1 || tab === 2) {
      if (Object.keys(templates).length === 0) {
        loadTemplates();
      }
    }
  }, [tab, page, limit]);

  const handleTabChange = (e, newTab) => {
    setTab(newTab);
    const tabParamMap = { 0: 'notification', 1: 'whatsapp', 2: 'sms', 3: 'logs' };
    setSearchParams({ tab: tabParamMap[newTab] || 'notification' }, { replace: true });
    if (newTab === 1 || newTab === 2) {
      if (users.length === 0) loadUsersAndBatches();
      if (Object.keys(templates).length === 0) loadTemplates();
    } else if (newTab === 3) {
      loadLogs();
    }
  };

  const loadQuota = async () => {
    try {
      const res = await gymsAPI.getMessageQuota();
      if (res.data.success) {
        setMessageQuota(res.data.data);
        if (res.data.data.packages && res.data.data.packages.length > 0) {
          const currentValid = res.data.data.packages.some(p => p.count === Number(topUpPackage));
          if (!currentValid) {
            setTopUpPackage(res.data.data.packages[0].count);
          }
        }
      }
    } catch (err) {
      console.error('Error loading message quota:', err);
    }
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await marketingAPI.getTemplates();
      const tmpls = res.data.data.templates || {};
      setTemplates(tmpls);
      const keys = Object.keys(tmpls);
      if (keys.length > 0) {
        if (!waTemplateKey) setWaTemplateKey(keys[0]);
        if (!smsTemplateKey) setSmsTemplateKey(keys[0]);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsersAndBatches = async () => {
    setUsersLoading(true);
    try {
      const [usersRes, batchesRes] = await Promise.allSettled([
        usersAPI.getAll({ limit: 1000 }),
        batchesAPI.getAll()
      ]);
      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data.data || []);
      }
      if (batchesRes.status === 'fulfilled') {
        setBatches(batchesRes.value.data.data || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await marketingAPI.getLogs({ page, limit });
      setLogs(res.data.data || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error('Error loading logs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Build available groups/segments from users and batches
  const availableGroups = useMemo(() => {
    const groups = [];

    // 1. Gym Batches
    if (batches && batches.length > 0) {
      batches.forEach(b => {
        const matchUsers = users.filter(u => u.batchName && u.batchName.toLowerCase() === b.name.toLowerCase());
        groups.push({
          id: `batch_${b._id || b.name}`,
          name: `Batch: ${b.name}${b.preferredTime ? ` (${b.preferredTime})` : ''}`,
          category: 'Batches / Time Slots',
          userIds: matchUsers.map(u => u._id),
          count: matchUsers.length,
        });
      });
    }

    // Also include any user batchNames not explicitly in Batches table
    const distinctBatchNames = [...new Set(users.map(u => u.batchName).filter(Boolean))];
    distinctBatchNames.forEach(bn => {
      if (!groups.some(g => g.name.toLowerCase().includes(bn.toLowerCase()))) {
        const matchUsers = users.filter(u => u.batchName === bn);
        groups.push({
          id: `batch_custom_${bn}`,
          name: `Batch: ${bn}`,
          category: 'Batches / Time Slots',
          userIds: matchUsers.map(u => u._id),
          count: matchUsers.length,
        });
      }
    });

    // 2. Membership Status
    const activeUsers = users.filter(u => u.membershipStatus === 'active' || (u.membershipEndDate && new Date(u.membershipEndDate) >= new Date()));
    groups.push({
      id: 'status_active',
      name: 'Active Members',
      category: 'Membership Status',
      userIds: activeUsers.map(u => u._id),
      count: activeUsers.length,
    });

    const expiredUsers = users.filter(u => u.membershipStatus === 'expired' || (u.membershipEndDate && new Date(u.membershipEndDate) < new Date()));
    groups.push({
      id: 'status_expired',
      name: 'Expired / Due Members',
      category: 'Membership Status',
      userIds: expiredUsers.map(u => u._id),
      count: expiredUsers.length,
    });

    const pausedUsers = users.filter(u => u.membershipStatus === 'paused');
    if (pausedUsers.length > 0) {
      groups.push({
        id: 'status_paused',
        name: 'Paused Members',
        category: 'Membership Status',
        userIds: pausedUsers.map(u => u._id),
        count: pausedUsers.length,
      });
    }

    // 3. Gender
    const maleUsers = users.filter(u => u.gender === 'male');
    if (maleUsers.length > 0) {
      groups.push({
        id: 'gender_male',
        name: 'Male Members',
        category: 'Gender',
        userIds: maleUsers.map(u => u._id),
        count: maleUsers.length,
      });
    }

    const femaleUsers = users.filter(u => u.gender === 'female');
    if (femaleUsers.length > 0) {
      groups.push({
        id: 'gender_female',
        name: 'Female Members',
        category: 'Gender',
        userIds: femaleUsers.map(u => u._id),
        count: femaleUsers.length,
      });
    }

    // 4. Premium Subscription
    const premiumUsers = users.filter(u => u.isPremium);
    if (premiumUsers.length > 0) {
      groups.push({
        id: 'premium_vip',
        name: 'Premium VIP Members',
        category: 'Subscription Plan',
        userIds: premiumUsers.map(u => u._id),
        count: premiumUsers.length,
      });
    }

    // 5. Staff & Roles
    const trainers = users.filter(u => ['trainer', 'coach'].includes(u.role));
    if (trainers.length > 0) {
      groups.push({
        id: 'role_trainers',
        name: 'Trainers & Coaches',
        category: 'Staff & Roles',
        userIds: trainers.map(u => u._id),
        count: trainers.length,
      });
    }

    return groups;
  }, [users, batches]);

  // Helper to extract unique user IDs from selected groups
  const getGroupUserIds = (selectedGroups) => {
    const idSet = new Set();
    selectedGroups.forEach(grp => {
      grp.userIds.forEach(id => idSet.add(id));
    });
    return Array.from(idSet);
  };

  const handleSendChannelMessage = async (channel) => {
    const isWhatsApp = channel === 'whatsapp';
    const sendMode = isWhatsApp ? waSendMode : smsSendMode;
    const selectedList = isWhatsApp ? waSelectedUsers : smsSelectedUsers;
    const selectedGroups = isWhatsApp ? waSelectedGroups : smsSelectedGroups;
    const audience = isWhatsApp ? waAudience : smsAudience;
    const templateKey = isWhatsApp ? waTemplateKey : smsTemplateKey;
    const setSending = isWhatsApp ? setWaSending : setSmsSending;

    if (!templateKey) {
      setSnackbar({ open: true, message: 'Please select a message template.', severity: 'warning' });
      return;
    }

    if (sendMode === 'selected' && selectedList.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one recipient.', severity: 'warning' });
      return;
    }

    if (sendMode === 'group' && selectedGroups.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one group.', severity: 'warning' });
      return;
    }

    setSending(true);
    try {
      const payload = {
        channel,
        templateKey,
      };

      if (sendMode === 'all') {
        payload.targetAudience = audience;
      } else if (sendMode === 'group') {
        const groupUserIds = getGroupUserIds(selectedGroups);
        if (groupUserIds.length === 0) {
          setSnackbar({ open: true, message: 'Selected group(s) have no members.', severity: 'warning' });
          setSending(false);
          return;
        }
        payload.userIds = groupUserIds;
      } else {
        if (selectedList.length === 1) {
          payload.userId = selectedList[0]._id;
        } else {
          payload.userIds = selectedList.map(u => u._id);
        }
      }

      const res = await marketingAPI.sendMessage(payload);
      setSnackbar({
        open: true,
        message: res.data.message || `${isWhatsApp ? 'WhatsApp' : 'SMS'} message sent successfully!`,
        severity: 'success'
      });

      if (sendMode === 'selected') {
        if (isWhatsApp) setWaSelectedUsers([]);
        else setSmsSelectedUsers([]);
      } else if (sendMode === 'group') {
        if (isWhatsApp) setWaSelectedGroups([]);
        else setSmsSelectedGroups([]);
      }
      loadQuota();
    } catch (err) {
      console.error(`Error sending ${channel} message:`, err);
      const errorMsg = err.response?.data?.message || `Failed to send ${channel} message`;
      if (err.response?.data?.code === 'MESSAGE_LIMIT_REACHED') {
        setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        setTopUpDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: errorMsg, severity: 'error' });
      }
    } finally {
      setSending(false);
    }
  };

  const handleTopUpSubmit = async () => {
    setToppingUp(true);
    try {
      const res = await gymsAPI.topUpMessages({ packageCount: parseInt(topUpPackage, 10) });
      if (res.data.success) {
        setSnackbar({
          open: true,
          message: `Successfully added +${topUpPackage} messages! New limit: ${res.data.data.maxMessages}`,
          severity: 'success'
        });
        setMessageQuota(res.data.data);
        setTopUpDialogOpen(false);
      }
    } catch (err) {
      console.error('Error topping up messages:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to top up messages',
        severity: 'error'
      });
    } finally {
      setToppingUp(false);
    }
  };

  const remainingMessages = Math.max(0, (messageQuota.maxMessages || 100) - (messageQuota.sentMessages || 0));

  const waResolvedGroupUsers = useMemo(() => getGroupUserIds(waSelectedGroups), [waSelectedGroups]);
  const smsResolvedGroupUsers = useMemo(() => getGroupUserIds(smsSelectedGroups), [smsSelectedGroups]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <PageHeader
        title="Communication Center"
        subtitle="Send push notifications, WhatsApp broadcasts, and SMS messages to single users, groups / batches, or all members."
        gradient="linear-gradient(90deg, #1D4ED8, #60A5FA)"
        actions={
          <Paper elevation={0} sx={{
            p: 1, px: 2,
            bgcolor: messageQuota.limitReached ? '#FEF2F2' : '#F0FDF4',
            border: '1px solid',
            borderColor: messageQuota.limitReached ? '#FCA5A5' : '#BBF7D0',
            borderRadius: 2,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: messageQuota.limitReached ? '#991B1B' : '#166534', textTransform: 'uppercase', fontSize: '0.6rem', display: 'block' }}>
                SMS & WhatsApp Quota
              </Typography>
              <Typography variant="body2" fontWeight={700} color={messageQuota.limitReached ? 'error.main' : '#14532D'} sx={{ fontSize: '0.8rem' }}>
                {messageQuota.sentMessages || 0} / {messageQuota.maxMessages || 100}
                {messageQuota.limitReached && <Chip size="small" label="Limit Reached" color="error" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
              </Typography>
            </Box>
            <Button variant="contained" size="small" color={messageQuota.limitReached ? 'error' : 'success'}
              startIcon={<AddShoppingCart sx={{ fontSize: '14px !important' }} />}
              onClick={() => setTopUpDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
              Top Up
            </Button>
          </Paper>
        }
      />

      {/* Tabs Bar */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, borderRadius: '8px 8px 0 0', bgcolor: '#fff' }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              minHeight: 52,
              px: 3,
            }
          }}
        >
          <Tab icon={<NotifIcon sx={{ mr: 1, fontSize: 20 }} />} iconPosition="start" label="Push Notification" />
          <Tab icon={<WhatsApp sx={{ mr: 1, fontSize: 20, color: '#25D366' }} />} iconPosition="start" label="WhatsApp" />
          <Tab icon={<Sms sx={{ mr: 1, fontSize: 20, color: '#0284C7' }} />} iconPosition="start" label="SMS" />
          <Tab icon={<Message sx={{ mr: 1, fontSize: 20 }} />} iconPosition="start" label="Message Logs" />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Push Notification ────────────────────────────────────────── */}
      {tab === 0 && (
        <Notifications hideHeader hideTabs />
      )}

      {/* ── Tab 1: WhatsApp ─────────────────────────────────────────────────── */}
      {tab === 1 && (
        <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WhatsApp sx={{ color: '#16A34A', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} color="#0F172A">
                  WhatsApp Messaging
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Send official WhatsApp messages to specific users, batches / groups, or broadcast to all members.
                </Typography>
              </Box>
            </Stack>

            {messageQuota.limitReached && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => setTopUpDialogOpen(true)}>
                    Top Up Now
                  </Button>
                }
                sx={{ mb: 3 }}
              >
                Message quota limit reached ({messageQuota.sentMessages}/{messageQuota.maxMessages}). Top up messages to continue.
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Recipient Mode Selection (3-way) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom color="#334155">
                  1. Choose Recipients
                </Typography>
                <ToggleButtonGroup
                  value={waSendMode}
                  exclusive
                  onChange={(e, val) => val && setWaSendMode(val)}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="selected" sx={{ px: 2, py: 1, fontWeight: 700, textTransform: 'none' }}>
                    <Person sx={{ fontSize: 18, mr: 1 }} /> Specific Users
                  </ToggleButton>
                  <ToggleButton value="group" sx={{ px: 2, py: 1, fontWeight: 700, textTransform: 'none' }}>
                    <Groups sx={{ fontSize: 18, mr: 1 }} /> Member Groups & Batches
                  </ToggleButton>
                  <ToggleButton value="all" sx={{ px: 2, py: 1, fontWeight: 700, textTransform: 'none' }}>
                    <People sx={{ fontSize: 18, mr: 1 }} /> All Users ({users.length})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              {/* Mode A: Specific Users */}
              {waSendMode === 'selected' && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={users}
                    loading={usersLoading}
                    value={waSelectedUsers}
                    onChange={(e, val) => setWaSelectedUsers(val)}
                    getOptionLabel={(option) => `${option.name || 'Unnamed'} (${option.phone || option.email || 'No contact'})`}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Users (Search by Name, Phone, or Email)"
                        placeholder={waSelectedUsers.length === 0 ? "Type to search members..." : "Add more members..."}
                        helperText="You can select one or multiple users to receive this WhatsApp message."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option._id}
                          label={`${option.name || 'Member'} (${maskPhone(option.phone)})`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))
                    }
                  />
                  {waSelectedUsers.length > 0 && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.8, display: 'block', fontWeight: 600 }}>
                      ✓ {waSelectedUsers.length} recipient{waSelectedUsers.length > 1 ? 's' : ''} selected
                    </Typography>
                  )}
                </Grid>
              )}

              {/* Mode B: Member Groups & Batches */}
              {waSendMode === 'group' && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={availableGroups}
                    groupBy={(option) => option.category}
                    getOptionLabel={(option) => `${option.name} (${option.count} members)`}
                    value={waSelectedGroups}
                    onChange={(e, val) => setWaSelectedGroups(val)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Group(s) / Batch(es)"
                        placeholder={waSelectedGroups.length === 0 ? "Choose groups or batches..." : "Add more groups..."}
                        helperText="Select one or multiple batches or membership groups."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={`${option.name} (${option.count})`}
                          size="small"
                          color="success"
                        />
                      ))
                    }
                  />
                  {waSelectedGroups.length > 0 && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.8, display: 'block', fontWeight: 700 }}>
                      ✓ {waResolvedGroupUsers.length} unique recipient member{waResolvedGroupUsers.length > 1 ? 's' : ''} across {waSelectedGroups.length} selected group{waSelectedGroups.length > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Grid>
              )}

              {/* Mode C: All Users Audience */}
              {waSendMode === 'all' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Target Member Group</InputLabel>
                    <Select
                      value={waAudience}
                      onChange={(e) => setWaAudience(e.target.value)}
                      label="Target Member Group"
                    >
                      <MenuItem value="all">All Members with Phone Number ({users.filter(u => u.phone).length})</MenuItem>
                      <MenuItem value="active">Active Members Only ({users.filter(u => u.membershipStatus === 'active' && u.phone).length})</MenuItem>
                      <MenuItem value="expired">Expired / Churned Members Only ({users.filter(u => u.membershipEndDate && new Date(u.membershipEndDate) < new Date() && u.phone).length})</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Template selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom color="#334155" sx={{ mt: 1 }}>
                  2. Select WhatsApp Template
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Message Template</InputLabel>
                  <Select
                    value={waTemplateKey}
                    onChange={(e) => setWaTemplateKey(e.target.value)}
                    label="Message Template"
                  >
                    {Object.keys(templates).map(key => (
                      <MenuItem key={key} value={key}>
                        {key.replace(/_/g, ' ').toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Template preview */}
              {waTemplateKey && templates[waTemplateKey] && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: '#F0FDF4',
                      border: '1px solid #DCFCE7',
                      borderRadius: 2.5
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <WhatsApp sx={{ color: '#16A34A', fontSize: 18 }} />
                      <Typography variant="subtitle2" fontWeight={700} color="#166534">
                        WhatsApp Message Preview
                      </Typography>
                    </Stack>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: 2,
                        fontFamily: 'inherit'
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#1E293B', lineHeight: 1.6 }}>
                        {templates[waTemplateKey]['whatsapp']?.body || 'No template body available for WhatsApp.'}
                      </Typography>
                    </Paper>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      💡 Variables like {'{{memberName}}'} and {'{{gymName}}'} will be dynamically populated for each recipient upon delivery.
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Send Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  color="success"
                  startIcon={waSending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  onClick={() => handleSendChannelMessage('whatsapp')}
                  disabled={
                    waSending ||
                    !waTemplateKey ||
                    messageQuota.limitReached ||
                    (waSendMode === 'selected' && waSelectedUsers.length === 0) ||
                    (waSendMode === 'group' && waSelectedGroups.length === 0)
                  }
                  sx={{
                    py: 1.5,
                    fontWeight: 800,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)'
                  }}
                >
                  {waSending
                    ? 'Sending WhatsApp Message…'
                    : messageQuota.limitReached
                    ? 'Message Quota Reached'
                    : waSendMode === 'all'
                    ? `Send WhatsApp Broadcast to ${waAudience.toUpperCase()} Members`
                    : waSendMode === 'group'
                    ? `Send WhatsApp to Group (${waResolvedGroupUsers.length} Members)`
                    : `Send WhatsApp to ${waSelectedUsers.length} Selected Member${waSelectedUsers.length > 1 ? 's' : ''}`
                  }
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Tab 2: SMS ──────────────────────────────────────────────────────── */}
      {tab === 2 && (
        <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#E0F2FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sms sx={{ color: '#0284C7', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} color="#0F172A">
                  SMS Messaging
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Send direct SMS messages to specific members, batches / groups, or broadcast to all registered members.
                </Typography>
              </Box>
            </Stack>

            {messageQuota.limitReached && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => setTopUpDialogOpen(true)}>
                    Top Up Now
                  </Button>
                }
                sx={{ mb: 3 }}
              >
                Message quota limit reached ({messageQuota.sentMessages}/{messageQuota.maxMessages}). Top up messages to continue.
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Recipient Mode Selection (3-way) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom color="#334155">
                  1. Choose Recipients
                </Typography>
                <ToggleButtonGroup
                  value={smsSendMode}
                  exclusive
                  onChange={(e, val) => val && setSmsSendMode(val)}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="selected" sx={{ px: 2, py: 1, fontWeight: 700, textTransform: 'none' }}>
                    <Person sx={{ fontSize: 18, mr: 1 }} /> Specific Users
                  </ToggleButton>
                  <ToggleButton value="group" sx={{ px: 2, py: 1, fontWeight: 700, textTransform: 'none' }}>
                    <Groups sx={{ fontSize: 18, mr: 1 }} /> Member Groups & Batches
                  </ToggleButton>
                  <ToggleButton value="all" sx={{ px: 2, py: 1, fontWeight: 700, textTransform: 'none' }}>
                    <People sx={{ fontSize: 18, mr: 1 }} /> All Users ({users.length})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              {/* Mode A: Specific Users */}
              {smsSendMode === 'selected' ? (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={users}
                    loading={usersLoading}
                    value={smsSelectedUsers}
                    onChange={(e, val) => setSmsSelectedUsers(val)}
                    getOptionLabel={(option) => `${option.name || 'Unnamed'} (${option.phone || option.email || 'No contact'})`}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Users (Search by Name, Phone, or Email)"
                        placeholder={smsSelectedUsers.length === 0 ? "Type to search members..." : "Add more members..."}
                        helperText="You can select one or multiple users to receive this SMS."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option._id}
                          label={`${option.name || 'Member'} (${maskPhone(option.phone)})`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    }
                  />
                  {smsSelectedUsers.length > 0 && (
                    <Typography variant="caption" color="primary.main" sx={{ mt: 0.8, display: 'block', fontWeight: 600 }}>
                      ✓ {smsSelectedUsers.length} recipient{smsSelectedUsers.length > 1 ? 's' : ''} selected
                    </Typography>
                  )}
                </Grid>
              ) : smsSendMode === 'group' ? (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={availableGroups}
                    groupBy={(option) => option.category}
                    getOptionLabel={(option) => `${option.name} (${option.count} members)`}
                    value={smsSelectedGroups}
                    onChange={(e, val) => setSmsSelectedGroups(val)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Group(s) / Batch(es)"
                        placeholder={smsSelectedGroups.length === 0 ? "Choose groups or batches..." : "Add more groups..."}
                        helperText="Select one or multiple batches or membership groups."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={`${option.name} (${option.count})`}
                          size="small"
                          color="primary"
                        />
                      ))
                    }
                  />
                  {smsSelectedGroups.length > 0 && (
                    <Typography variant="caption" color="primary.main" sx={{ mt: 0.8, display: 'block', fontWeight: 700 }}>
                      ✓ {smsResolvedGroupUsers.length} unique recipient member{smsResolvedGroupUsers.length > 1 ? 's' : ''} across {smsSelectedGroups.length} selected group{smsSelectedGroups.length > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Grid>
              ) : (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Target Member Group</InputLabel>
                    <Select
                      value={smsAudience}
                      onChange={(e) => setSmsAudience(e.target.value)}
                      label="Target Member Group"
                    >
                      <MenuItem value="all">All Members with Phone Number ({users.filter(u => u.phone).length})</MenuItem>
                      <MenuItem value="active">Active Members Only ({users.filter(u => u.membershipStatus === 'active' && u.phone).length})</MenuItem>
                      <MenuItem value="expired">Expired / Churned Members Only ({users.filter(u => u.membershipEndDate && new Date(u.membershipEndDate) < new Date() && u.phone).length})</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Template selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom color="#334155" sx={{ mt: 1 }}>
                  2. Select SMS Template
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Message Template</InputLabel>
                  <Select
                    value={smsTemplateKey}
                    onChange={(e) => setSmsTemplateKey(e.target.value)}
                    label="Message Template"
                  >
                    {Object.keys(templates).map(key => (
                      <MenuItem key={key} value={key}>
                        {key.replace(/_/g, ' ').toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Template preview */}
              {smsTemplateKey && templates[smsTemplateKey] && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: '#F0F9FF',
                      border: '1px solid #E0F2FE',
                      borderRadius: 2.5
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Sms sx={{ color: '#0284C7', fontSize: 18 }} />
                      <Typography variant="subtitle2" fontWeight={700} color="#0369A1">
                        SMS Message Preview
                      </Typography>
                    </Stack>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: 2,
                        fontFamily: 'inherit'
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#1E293B', lineHeight: 1.6 }}>
                        {templates[smsTemplateKey]['sms']?.body || 'No template body available for SMS.'}
                      </Typography>
                    </Paper>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      💡 Variables like {'{{memberName}}'} and {'{{gymName}}'} will be dynamically populated for each recipient upon delivery.
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Send Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  color="primary"
                  startIcon={smsSending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  onClick={() => handleSendChannelMessage('sms')}
                  disabled={
                    smsSending ||
                    !smsTemplateKey ||
                    messageQuota.limitReached ||
                    (smsSendMode === 'selected' && smsSelectedUsers.length === 0) ||
                    (smsSendMode === 'group' && smsSelectedGroups.length === 0)
                  }
                  sx={{
                    py: 1.5,
                    fontWeight: 800,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)'
                  }}
                >
                  {smsSending
                    ? 'Sending SMS Message…'
                    : messageQuota.limitReached
                    ? 'Message Quota Reached'
                    : smsSendMode === 'all'
                    ? `Send SMS Broadcast to ${smsAudience.toUpperCase()} Members`
                    : smsSendMode === 'group'
                    ? `Send SMS to Group (${smsResolvedGroupUsers.length} Members)`
                    : `Send SMS to ${smsSelectedUsers.length} Selected Member${smsSelectedUsers.length > 1 ? 's' : ''}`
                  }
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Tab 3: Message Logs ────────────────────────────────────────────── */}
      {tab === 3 && (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Box>
              <Typography variant="h6" fontWeight={800} color="#0F172A">
                Message Delivery Logs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Historical records of all WhatsApp and SMS messages dispatched from the system.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={loadLogs}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Refresh
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Recipient Member</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Template / Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading message logs...</Typography>
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <InfoOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No message logs recorded yet</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map(log => (
                    <TableRow key={log._id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {new Date(log.sentAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(log.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {log.userId?.name || 'Member'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {maskPhone(log.phone)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={log.channel === 'whatsapp' ? <WhatsApp fontSize="small" /> : <Sms fontSize="small" />}
                          label={(log.channel || 'unknown').toUpperCase()}
                          color={log.channel === 'whatsapp' ? 'success' : 'primary'}
                          variant="outlined"
                          sx={{ fontWeight: 700, borderRadius: 1.5 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(log.templateKey || 'direct').replace(/_/g, ' ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={log.status === 'sent' ? <CheckCircle fontSize="small" /> : <ErrorOutline fontSize="small" />}
                          label={(log.status || 'unknown').toUpperCase()}
                          color={log.status === 'sent' ? 'success' : 'error'}
                          sx={{ fontWeight: 700, borderRadius: 1.5 }}
                        />
                        {log.reason && (
                          <Typography variant="caption" display="block" color="error.main" sx={{ mt: 0.5, maxWidth: 220 }}>
                            {log.reason}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <PaginationBar
              page={page}
              pages={pages}
              total={total}
              limit={limit}
              onPage={setPage}
              onLimit={v => { setLimit(v); setPage(1); }}
            />
          </Box>
        </Paper>
      )}

      {/* Pay to Increase Messages Dialog */}
      <Dialog open={topUpDialogOpen} onClose={() => setTopUpDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Pay to Increase Messages</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            Your gym has reached or is near its message limit ({messageQuota.sentMessages}/{messageQuota.maxMessages}). Top up your account to continue sending SMS & WhatsApp messages.
          </Typography>

          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Select Message Package</InputLabel>
            <Select
              value={topUpPackage}
              label="Select Message Package"
              onChange={(e) => setTopUpPackage(e.target.value)}
            >
              {(messageQuota.packages && messageQuota.packages.length > 0
                ? messageQuota.packages
                : [
                    { count: 100, price: 99 },
                    { count: 500, price: 449 },
                    { count: 1000, price: 799 }
                  ]
              ).map((pkg) => (
                <MenuItem key={pkg.count} value={pkg.count}>
                  +{pkg.count} Messages (₹{pkg.price})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F9FAFB', borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">Current Message Cap:</Typography>
              <Typography variant="body2" fontWeight={700}>{messageQuota.maxMessages} Messages</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">New Message Cap:</Typography>
              <Typography variant="body2" fontWeight={700} color="primary.main">
                {(messageQuota.maxMessages || 100) + parseInt(topUpPackage, 10)} Messages
              </Typography>
            </Stack>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setTopUpDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={toppingUp ? <CircularProgress size={18} color="inherit" /> : <AddShoppingCart />}
            onClick={handleTopUpSubmit}
            disabled={toppingUp}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            {toppingUp ? 'Processing...' : 'Pay & Increase Messages'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity || 'success'}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
